import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { Subject, throttleTime } from 'rxjs';

import { MyBetsTab } from '../../models/my-bets-tab';
import { MyBetsLoaderService } from '../../services/my-bets-loader.service';
import { MyBetsTabsService } from '../../services/my-bets-tabs.service';

@Component({
    selector: 'ms-my-bets-navigation',
    templateUrl: 'my-bets-navigation.html',
    styleUrl: 'my-bets-navigation.scss',
})
export class MyBetsNavigationComponent implements OnInit {
    private sitecoreMyBets = inject(SitecoreMyBetsConfig);
    private myBetsTabService = inject(MyBetsTabsService);
    private trackingService = inject(TrackingService);
    private myBetsLoader = inject(MyBetsLoaderService);
    private myBetsConfig = inject(MyBetsConfig);
    private readonly destroyRef = inject(DestroyRef);

    tabNames: Record<BetslipListType, string> = {
        [BetslipListType.Live]: this.sitecoreMyBets.myBets.Live || 'Live',
        [BetslipListType.Open]: this.sitecoreMyBets.myBets.OPEN || 'Open',
        [BetslipListType.Settled]: this.sitecoreMyBets.myBets.Settled || 'Settled',
    } as Record<BetslipListType, string>;

    private listTypeToAttribute = {
        [BetslipListType.Live]: 'mybets-tab-live',
        [BetslipListType.Settled]: 'mybets-tab-settled',
    };

    private listTypeToPageName = {
        [BetslipListType.Live]: 'myBets_Live',
        [BetslipListType.Open]: 'myBets_Open',
    };

    isMyBetsTabRedesignEnabled = this.myBetsConfig.isMyBetTabRedesignEnabled;

    private click$ = new Subject<MyBetsTab>();

    ngOnInit(): void {
        this.click$.pipe(throttleTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe((tab: MyBetsTab) => {
            this.myBetsTabService.selectedTab = tab;
            const pageName = this.listTypeToPageName[tab.typeFilter as BetslipListType.Open] || 'myBets_Settled';
            this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
                [trackingConstants.PAGE_NAME]: pageName,
                [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
            });
        });
    }

    get tabs(): MyBetsTab[] {
        return this.myBetsTabService.betslipTabs;
    }

    onSelectTab(tab: MyBetsTab): void {
        if (tab.typeFilter === BetslipListType.Open) {
            this.myBetsLoader.trackOpenBetsDetails(tab.count);
        }
        if (this.myBetsTabService.activeTab?.typeFilter === tab.typeFilter) {
            return;
        }
        this.click$.next(tab);
    }

    getTabTitle(tab: MyBetsTab): string {
        const tabName = this.tabNames[tab.typeFilter];
        return tab.count ? `${tabName} (${tab.count})` : tabName;
    }

    getActiveOption(): number {
        const tabLength = this.tabs.findIndex((tab) => tab.selected === true);
        if (tabLength < 0) {
            return this.tabs.length - 1;
        }
        return this.tabs.findIndex((tab) => tab.selected === true || tab.count > 0);
    }

    onSelectionChange(event: number): MyBetsTab {
        const selectedTab = this.tabs[event];
        this.onSelectTab(selectedTab);
        return selectedTab;
    }

    getDataAtributteName(typeFilter: BetslipListType): string {
        return this.listTypeToAttribute[typeFilter as BetslipListType.Settled] || 'mybets-tab-open';
    }
}
